import React, {useEffect, useState} from "react";
import {getQueryParameter} from "../../../../libraries/util/QueryParameterHelper";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {POST_JOB_DEFINITION} from "../graphql/mutations/gqlPostJobDefinition";
import {IJobDefinition, InputType} from "../../models/JobDefinition";
import {IJobDefinitionStep} from "../../models/JobDefinitionStep";
import {IPostJobDefinitionInput} from "../../models/inputs/PostJobDefinitionInput";
import {IPostJobDefinitionStepInput} from "../../models/inputs/PostJobDefinitionStepInput";
import {GET_JOB_DEFINITIONS} from "../graphql/queries/gqlGetJobDefinitions";
import {LoaderForm} from "../loader-form/LoaderForm";
import {GET_JOB_STEPS} from "../graphql/queries/gqlGetJobDefinitionSteps";
import {POST_JOB_STEP} from "../graphql/mutations/gqlPostJobStep";
import {JobDefinitionForm} from "./JobDefinitionForm";
import {JobDefinitionStepsTable} from "./JobDefinitionStepsTable";
import {GenericModal} from "../generic-modal/GenericModal";
import {JobDefinitionStepForm} from "./JobDefinitionStepForm";
import {Button, ButtonGroup} from "@mui/material";
import {PentaSplit} from "../../../../components/common/PentaSplit";
import {DELETE_JOB_STEP} from "../graphql/mutations/gqlDeleteJobStep";
import {useNavigate} from "react-router-dom";
import {DELETE_JOB_DEFINITION} from "../graphql/mutations/gqlDeleteJobDefinition";
import {ConfirmationDialog} from "../../../../components/common/confirmation-dialog/ConfirmationDialog";
import {toast} from "react-toastify";
import {ITag} from "../../models/Tag";

export const JobDefinitionFormWrapper = () => {
	const navigate = useNavigate();
	const jobDefinitionId = +(getQueryParameter("jobDefinitionId") ?? "0");
	const [loading, setLoading] = useState(true);
	const [jobDefinitionStepId, setJobDefinitionStepId] = useState<number>();
	
	const [jobDefinition, setJobDefinition] = useState<IJobDefinition>({
		description: "",
		jobSteps: [],
		name: "",
		inputType: InputType.Anva,
		id: jobDefinitionId,
	});
	const [deletedSteps, setDeletedSteps] = useState<IJobDefinitionStep[]>([]);
	
	const convertDataToInputRequest = (data: IJobDefinition): {
		jobDefinition: IPostJobDefinitionInput;
		jobDefinitionsSteps: IPostJobDefinitionStepInput[];
	} => {
		return {
			jobDefinitionsSteps: data.jobSteps.map(it => {
				return {
					id: it.id > 999999 ? 0 : it.id,
					description: it.description,
					jobDefinition: data.id,
					inputStep: it.inputStep?.id,
					jobSettings: it.jobSettings?.id ?? 0,
					customSettings: it.customSettings,
					concurrency: it.concurrency,
					order: it.order,
					processor: it.processor,
					startMode: it.startMode,
				};
			}),
			jobDefinition: {
				id: data.id,
				description: data.description,
				name: data.name,
				inputType: data.inputType,
				tags: data.tags
			}
		};
	};
	
	const deleteDefinition = async () => {
		await graphQLServices.querytoolClient.mutate({
			mutation: DELETE_JOB_DEFINITION,
			variables: {input: {id: jobDefinition.id}},
		}).catch(_=> toast.error("Failed to remove definition"))
			.then(() => toast.success("Definition deleted")).then(() =>
				navigate("..")
			);
	};
	
	const submit = async () => {
		await graphQLServices.querytoolClient.mutate({
			mutation: POST_JOB_DEFINITION,
			variables: {input: convertDataToInputRequest(jobDefinition).jobDefinition},
		});
		for (const step of convertDataToInputRequest(jobDefinition).jobDefinitionsSteps) {
			await graphQLServices.querytoolClient.mutate({
				mutation: POST_JOB_STEP,
				variables: {input: step}
			});
		}
		for (const step of deletedSteps) {
			await graphQLServices.querytoolClient.mutate({
				mutation: DELETE_JOB_STEP,
				variables: {input: {id: step.id}}
			});
		}
		navigate("/querytool/jobdefinitions");
	};
	
	const getJobDefinition = () => {
		return graphQLServices.querytoolClient
			.query({
				query: GET_JOB_DEFINITIONS,
				variables: {
					"where": {
						"and": {
							"id": {
								"eq": jobDefinitionId,
							},
						},
					},
				},
			})
			.then((result) => {
				const item = result.data.jobDefinitions.nodes.filter((it: IJobDefinition) => it.id === jobDefinitionId)[0];
				setJobDefinition((prevJobState) => ({
					...prevJobState,
					name: item.name,
					id: item.id,
					description: item.description,
					inputType: item.inputType,
					tags: item.tags.map((tag:ITag)=> {
						return {key: tag.key, value: tag.value, id: tag.id
						};
					})
				}));
			});
	};
	const getJobDefinitionSteps = () => {
		return graphQLServices.querytoolClient
			.query({
				query: GET_JOB_STEPS,
				variables: {
					"filter": {
						"and": {
							"jobDefinition": {
								"id": {
									"eq": jobDefinitionId
								}
							}
						}
					}
				},
			})
			.then(results => setJobDefinition((prevJobDefinition) => ({
				...prevJobDefinition,
				jobSteps: results?.data?.jobSteps?.nodes ?? [],
			})));
	};
	
	useEffect(() => {
		if (jobDefinitionId !== 0) {
			Promise.allSettled([
				getJobDefinition(),
				getJobDefinitionSteps(),
			])
				.finally(() => setLoading(false));
		} else {
			setLoading(false);
		}
		// eslint-disable-next-line
	}, []);
	
	if (loading) {
		return <LoaderForm title={"Job Definition"}/>;
	}
	
	return (
		<>
			<div>
				
				<PentaSplit
					center={
						<>
							<JobDefinitionForm jobDefinition={jobDefinition} setJobDefinition={setJobDefinition}/>
							<JobDefinitionStepsTable
								steps={jobDefinition.jobSteps}
								setSteps={(steps) => setJobDefinition({...jobDefinition, jobSteps: steps})}
								deletedSteps={deletedSteps}
								setDeletedSteps={setDeletedSteps}
								setActiveRow={(id: number) => setJobDefinitionStepId(id)}
							/>
						</>
					}
					top={
						<>
							<ButtonGroup>
								<Button onClick={submit}>Save Changes</Button>
								{jobDefinition.id > 0 &&
									<ConfirmationDialog
										onSuccess={deleteDefinition}
										dialogTitle={"Are you sure?"}
										buttonTitle={"Delete Definition"}
										buttonProps={{ variant: "outlined" }}
										dialogDescription={
											"This will delete this definition"
										}
									/>
								}
							
							</ButtonGroup>
						</>
					}
				/>
			
			
			</div>
			
			<GenericModal
				open={!!jobDefinitionStepId}
				onClose={() => setJobDefinitionStepId(undefined)}
				title="Update Job Definition Step"
			>
				<JobDefinitionStepForm
					step={jobDefinition.jobSteps.filter(it => it.id === jobDefinitionStepId)[0]}
					setStep={(step) => {
						setJobDefinition((prevState) => ({
							...prevState,
							jobSteps: [...prevState.jobSteps.filter(it => it.id !== jobDefinitionStepId), step]
						}));
					}}
					steps={jobDefinition.jobSteps}
					jobDefinitionId={jobDefinition.id}
				/>
			</GenericModal>
		</>
	);
};
